import { openBrowserPopup } from 'Helpers/browserPopup'
import { notifyParent } from 'Helpers/notifyParent'
import {
  sessionId,
  STORE_KEY_ACCOUNT,
  STORE_KEY_FEATURES,
  storeGet
} from 'mailup-react-helpers'
import {
  Badge,
  BADGE_TYPE_WARNING,
  Flex,
  FlexItem,
  HelpIcon,
  Icon,
  Link,
  LINK_VARIANT_DEFAULT,
  LINK_VARIANT_EXTRALIGHT,
  MESSAGE_BOX_VARIANT_INFO,
  MessageBox,
  Panel,
  SKELETON_LOADER_VARIANT_ONWHITE,
  SkeletonLoader,
  Text,
  TEXT_VARIANT_BOLD,
  TEXT_VARIANT_ITALIC_LIGHT,
  TooltipUtils,
  TruncateAndTooltip
} from 'mailup-web-components'
import { manageFooterCallback } from 'Pages/OverviewMessage/recoil/callbacks/callbacks'
import { useEffect } from 'react'
import { useIntl } from 'react-intl'
import { useParams } from 'react-router-dom'
import { useRecoilCallback, useRecoilValue } from 'recoil'
import { footerState } from '../recoil/state/message'

const BoxFooter = () => {
  const intl = useIntl()
  const { id } = useParams()
  const footerInOverview =
    storeGet(STORE_KEY_FEATURES).indexOf('FooterInOverview') >= 0
  const { IsTrial, IsRetailer } = storeGet(STORE_KEY_ACCOUNT)
  const manageFooter = useRecoilCallback(manageFooterCallback, [])
  const footer = useRecoilValue(footerState)

  useEffect(() => {
    if (footerInOverview) {
      manageFooter(id, IsTrial, IsRetailer)
    }
  }, [])

  function goToListSetting() {
    notifyParent('redirectMainFrame', ['form_upd_nl.aspx?tab=mittenti'])
  }

  function goToPreview() {
    openBrowserPopup(`/${sessionId}/messages/email/${id}/preview`, {
      goToFooter: true
    })
  }

  if (footerInOverview) {
    return (
      <>
        <TooltipUtils
          id='footerBoxTooltip'
          type='light'
          effect='solid'
          className='c-tooltip'
        >
          {intl.formatMessage(
            { id: 'Load.SavedEmailWizard.FooterBoxTooltip' },
            {
              linebreak: <br />
            }
          )}
        </TooltipUtils>
        <Panel>
          <Flex
            margin='0 0 8px 0'
            columnGap='4px'
            height='24px'
            alignItems='flex-end'
          >
            <Text variant={TEXT_VARIANT_BOLD}>
              {intl.formatMessage({
                id: 'Load.SavedEmailWizard.FooterBoxTitle'
              })}
            </Text>
            <HelpIcon
              text={intl.formatMessage(
                { id: 'Load.SavedEmailWizard.FooterBoxTooltip' },
                { linebreak: '\n' }
              )}
            />
          </Flex>
          {footer.state === 'loading' && (
            <SkeletonLoader variant={SKELETON_LOADER_VARIANT_ONWHITE} />
          )}
          {footer.state === 'error' && (
            <Flex alignItems='center' columnGap='8px'>
              <Text variant={TEXT_VARIANT_ITALIC_LIGHT} htmlElement='span'>
                <Icon family='fa' icon='exclamation-triangle' />
              </Text>
              <Text variant={TEXT_VARIANT_ITALIC_LIGHT} htmlElement='span'>
                {intl.formatMessage({ id: 'Load.Common.LoadDataError' })}
              </Text>
            </Flex>
          )}

          {footer.state === 'ready' && (
            <>
              {footer.isCompleted && (
                <Flex columnGap='8px'>
                  <FlexItem width='100px' flexBasis='70%'>
                    {footer.data && (
                      <TruncateAndTooltip
                        text={footer.data}
                        tooltip={{ force: false, text: footer.dataTooltip }}
                      />
                    )}
                  </FlexItem>
                  <FlexItem width='100px' flexBasis='30%'>
                    <Link
                      variant={LINK_VARIANT_DEFAULT}
                      data-mu-test='footer-trail-complete-goToListSetting'
                      onClick={goToListSetting}
                    >
                      {intl.formatMessage({ id: 'Load.MailupCommon.Edit' })}
                    </Link>
                  </FlexItem>
                </Flex>
              )}
              {!footer.isCompleted && (
                <>
                  {(IsTrial || IsRetailer) && (
                    <Flex flexDirection='column' rowGap='8px'>
                      <Text>
                        {intl.formatMessage({
                          id: 'Load.SavedEmailWizard.FooterBoxTextNoInfo'
                        })}
                      </Text>
                      <FlexItem>
                        <Flex columnGap='16px'>
                          <Badge
                            type={BADGE_TYPE_WARNING}
                            text={intl.formatMessage({
                              id: 'Load.SavedEmailWizard.FooterBoxStatus'
                            })}
                          />
                          <Link
                            data-mu-test='footer-trail-incomplete-goToListSetting'
                            variant={LINK_VARIANT_EXTRALIGHT}
                            onClick={goToListSetting}
                          >
                            {intl.formatMessage({
                              id: 'Load.MailupCommon.Add'
                            })}
                          </Link>
                        </Flex>
                      </FlexItem>
                    </Flex>
                  )}
                  {!IsTrial && !IsRetailer && (
                    <>
                      {footer.isCompletedAfterInit && (
                        <>
                          <Flex columnGap='8px' margin='0 0 8px 0'>
                            <FlexItem width='100px' flexBasis='70%'>
                              <TruncateAndTooltip text={footer.data} />
                            </FlexItem>
                            <FlexItem width='100px' flexBasis='30%'>
                              <Link
                                data-mu-test='footer-trail-complete-goToListSetting'
                                variant={LINK_VARIANT_DEFAULT}
                                onClick={goToListSetting}
                              >
                                {intl.formatMessage({
                                  id: 'Load.MailupCommon.Edit'
                                })}
                              </Link>
                            </FlexItem>
                          </Flex>
                          <MessageBox
                            title={intl.formatMessage(
                              {
                                id: 'Load.SavedEmailWizard.FooterBoxInfoWarning'
                              },
                              {
                                link: chunks => (
                                  <a
                                    key={chunks}
                                    onClick={goToPreview}
                                    data-mu-test='footer-trail-complete-goToPreview'
                                  >
                                    {chunks}{' '}
                                  </a>
                                )
                              }
                            )}
                            variant={MESSAGE_BOX_VARIANT_INFO}
                          />
                        </>
                      )}
                      {!footer.isCompletedAfterInit && (
                        <Flex flexDirection='column' rowGap='8px'>
                          <Text>
                            {intl.formatMessage({
                              id: 'Load.SavedEmailWizard.FooterBoxTextNoInfo'
                            })}
                          </Text>
                          <FlexItem>
                            <Flex columnGap='16px'>
                              <Badge
                                type={BADGE_TYPE_WARNING}
                                text={intl.formatMessage({
                                  id: 'Load.SavedEmailWizard.FooterBoxStatus'
                                })}
                              />
                              <Link
                                data-mu-test='footer-trail-incomplete-goToListSetting'
                                variant={LINK_VARIANT_EXTRALIGHT}
                                onClick={goToListSetting}
                              >
                                {intl.formatMessage({
                                  id: 'Load.MailupCommon.Add'
                                })}
                              </Link>
                            </Flex>
                          </FlexItem>
                        </Flex>
                      )}
                    </>
                  )}
                </>
              )}
            </>
          )}
        </Panel>
      </>
    )
  } else {
    return <></>
  }
}

export default BoxFooter
